/**
 * ToolCallIndicator — Ink components for tool call display
 *
 * Renders tool calls in a bordered group with:
 * - Status icon (spinner / ✓ / ✗)
 * - Human-readable name + contextual description
 * - Collapsible inline result (diffs, shell output, grep hits)
 *
 * Inspired by gemini-cli's ToolGroupMessage / ToolMessage pattern.
 */

import React from 'react';
import { Box, Text } from 'ink';
import InkSpinner from 'ink-spinner';
import { COLORS, TOOL_STATUS, DIFF_MAX_LINES, SHELL_OUTPUT_MAX_LINES } from '../theme/ink-theme.js';

// ──────────────────────────────────────────────────────────────────
// Tool name helpers
// ──────────────────────────────────────────────────────────────────

const TOOL_META: Record<string, { label: string; icon: string }> = {
    paean_edit_file:       { label: 'Edit',         icon: '✎' },
    paean_grep:            { label: 'Search',       icon: '⌕' },
    paean_glob:            { label: 'Glob',         icon: '◎' },
    paean_read_many_files: { label: 'Read Files',   icon: '⊞' },
    paean_read_file:       { label: 'Read',         icon: '▸' },
    paean_write_file:      { label: 'Write',        icon: '✏' },
    paean_list_directory:  { label: 'List Dir',     icon: '▤' },
    paean_execute_shell:   { label: 'Shell',        icon: '$' },
    paean_web_fetch:       { label: 'Fetch',        icon: '⇣' },
    paean_memory:          { label: 'Memory',       icon: '◆' },
    paean_download_file:   { label: 'Download',     icon: '⇣' },
    paean_check_process:   { label: 'Process',      icon: '◉' },
    paean_kill_process:    { label: 'Kill',         icon: '✕' },
    paean_invoke_cli_agent:{ label: 'Agent',        icon: '∩' },
    paean_list_cli_agents: { label: 'Agents',       icon: '≡' },
    paean_setup_a8e:       { label: 'Setup',        icon: '⚡' },
};

export function formatToolName(name: string): string {
    const meta = TOOL_META[name];
    if (meta) return meta.label;

    let clean = name.replace(/^paean_/, '');
    clean = clean.replace(/([a-z])([A-Z])/g, '$1 $2');
    clean = clean.replace(/_/g, ' ');
    return clean.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function getToolIcon(name: string): string {
    return TOOL_META[name]?.icon ?? '⚙';
}

/** Extract a short human-readable description from tool args */
function getToolDescription(name: string, args?: Record<string, unknown>): string {
    if (!args) return '';
    switch (name) {
        case 'paean_edit_file': {
            const fp = args.filePath as string | undefined;
            return fp ? shortPath(fp) : '';
        }
        case 'paean_read_file':
        case 'paean_write_file': {
            const fp = args.filePath as string | undefined;
            return fp ? shortPath(fp) : '';
        }
        case 'paean_grep': {
            const pattern = args.pattern as string | undefined;
            return pattern ? `/${pattern.length > 30 ? pattern.slice(0, 27) + '…' : pattern}/` : '';
        }
        case 'paean_glob': {
            const pattern = args.pattern as string | undefined;
            return pattern ?? '';
        }
        case 'paean_execute_shell': {
            const cmd = args.command as string | undefined;
            if (!cmd) return '';
            return cmd.length > 50 ? cmd.slice(0, 47) + '…' : cmd;
        }
        case 'paean_read_many_files': {
            const paths = args.paths as string[] | undefined;
            return paths ? `${paths.length} file${paths.length !== 1 ? 's' : ''}` : '';
        }
        case 'paean_web_fetch': {
            const url = args.url as string | undefined;
            return url ? (url.length > 40 ? url.slice(0, 37) + '…' : url) : '';
        }
        case 'paean_memory': {
            const fact = args.fact as string | undefined;
            return fact ? (fact.length > 40 ? fact.slice(0, 37) + '…' : fact) : '';
        }
        case 'paean_list_directory': {
            const dp = args.dirPath as string | undefined;
            return dp ? shortPath(dp) : '';
        }
        default:
            return '';
    }
}

function shortPath(p: string): string {
    const cwd = process.cwd();
    if (p.startsWith(cwd)) p = p.slice(cwd.length + 1);
    const home = process.env['HOME'] ?? '';
    if (home && p.startsWith(home)) p = '~' + p.slice(home.length);
    return p;
}

// ──────────────────────────────────────────────────────────────────
// Result rendering helpers
// ──────────────────────────────────────────────────────────────────

/** Render a collapsed summary line for completed tool results */
function ResultSummary({ name, result }: { name: string; result?: unknown }): React.ReactElement | null {
    const res = result as Record<string, unknown> | undefined;
    if (!res) return null;

    if (name === 'paean_edit_file' && res.success && res.stats) {
        const stats = res.stats as { linesAdded?: number; linesRemoved?: number };
        return (
            <Text>
                <Text color={COLORS.success}>+{stats.linesAdded ?? 0}</Text>
                <Text color={COLORS.error}> -{stats.linesRemoved ?? 0}</Text>
            </Text>
        );
    }

    if (name === 'paean_grep' && res.success) {
        const count = (res.matchCount as number) ?? 0;
        return <Text dimColor>{count} match{count !== 1 ? 'es' : ''}</Text>;
    }

    if (name === 'paean_glob' && res.success) {
        const count = (res.fileCount as number) ?? 0;
        return <Text dimColor>{count} file{count !== 1 ? 's' : ''}</Text>;
    }

    if (name === 'paean_read_many_files' && res.success) {
        const count = (res.filesRead as number) ?? 0;
        return <Text dimColor>{count} file{count !== 1 ? 's' : ''} read</Text>;
    }

    if (name === 'paean_execute_shell') {
        if (res.exitCode !== undefined && res.exitCode !== 0) {
            return <Text color={COLORS.error}>exit {String(res.exitCode)}</Text>;
        }
        if (res.output && typeof res.output === 'string') {
            const lines = (res.output as string).split('\n').length;
            return <Text dimColor>{lines} line{lines !== 1 ? 's' : ''}</Text>;
        }
    }

    if (res.success === false && res.error) {
        const errMsg = String(res.error);
        return <Text color={COLORS.error}>{errMsg.length > 50 ? errMsg.slice(0, 47) + '…' : errMsg}</Text>;
    }

    return null;
}

/** Render the collapsible inline result content (diffs, shell output) */
function InlineResult({ name, result }: { name: string; result?: unknown }): React.ReactElement | null {
    const res = result as Record<string, unknown> | undefined;
    if (!res) return null;

    // Edit file → show compact diff
    if (name === 'paean_edit_file' && res.success && res.diff) {
        const diffStr = res.diff as string;
        return <CompactDiff diff={diffStr} maxLines={DIFF_MAX_LINES} />;
    }

    // Shell → show compact output
    if (name === 'paean_execute_shell' && res.output && typeof res.output === 'string') {
        return <CompactOutput output={res.output as string} maxLines={SHELL_OUTPUT_MAX_LINES} />;
    }

    return null;
}

/** Colored compact diff display */
function CompactDiff({ diff, maxLines }: { diff: string; maxLines: number }): React.ReactElement {
    const lines = diff.split('\n');
    const displayLines: Array<{ text: string; type: 'add' | 'del' | 'hunk' | 'ctx' }> = [];
    for (const line of lines) {
        if (line.startsWith('@@')) {
            displayLines.push({ text: line, type: 'hunk' });
        } else if (line.startsWith('+') && !line.startsWith('+++')) {
            displayLines.push({ text: line, type: 'add' });
        } else if (line.startsWith('-') && !line.startsWith('---')) {
            displayLines.push({ text: line, type: 'del' });
        }
    }

    const truncated = displayLines.length > maxLines;
    const shown = truncated ? displayLines.slice(0, maxLines) : displayLines;

    return (
        <Box flexDirection="column" paddingLeft={1}>
            {shown.map((l, i) => (
                <Box key={i} height={1} overflow="hidden">
                    {l.type === 'add' && <Text color={COLORS.success}>{l.text}</Text>}
                    {l.type === 'del' && <Text color={COLORS.error}>{l.text}</Text>}
                    {l.type === 'hunk' && <Text color="cyan" dimColor>{l.text}</Text>}
                </Box>
            ))}
            {truncated && (
                <Text dimColor>  … {displayLines.length - maxLines} more lines</Text>
            )}
        </Box>
    );
}

/** Truncated shell output display */
function CompactOutput({ output, maxLines }: { output: string; maxLines: number }): React.ReactElement {
    const lines = output.split('\n');
    const truncated = lines.length > maxLines;
    // Show last N lines (tail) for shell output — more useful than head
    const shown = truncated ? lines.slice(-maxLines) : lines;

    return (
        <Box flexDirection="column" paddingLeft={1}>
            {truncated && (
                <Text dimColor>  … {lines.length - maxLines} lines above</Text>
            )}
            {shown.map((l, i) => (
                <Box key={i} height={1} overflow="hidden">
                    <Text dimColor>{l}</Text>
                </Box>
            ))}
        </Box>
    );
}

// ──────────────────────────────────────────────────────────────────
// Single tool call row
// ──────────────────────────────────────────────────────────────────

export interface ToolCallRecord {
    id: string;
    name: string;
    type: 'tool' | 'mcp';
    serverName?: string;
    status: 'pending' | 'completed' | 'error';
    timestamp: number;
    args?: Record<string, unknown>;
    result?: unknown;
    durationMs?: number;
}

interface ToolCallIndicatorProps {
    name: string;
    type?: 'tool' | 'mcp';
    serverName?: string;
    status?: 'pending' | 'completed' | 'error';
    args?: Record<string, unknown>;
    result?: unknown;
    durationMs?: number;
}

export const ToolCallIndicator: React.FC<ToolCallIndicatorProps> = ({
    name,
    serverName,
    status = 'pending',
    args,
    result,
    durationMs,
}) => {
    const icon = getToolIcon(name);
    const displayName = formatToolName(name);
    const description = getToolDescription(name, args);

    const statusColor = status === 'error' ? COLORS.error
        : status === 'completed' ? COLORS.success
        : COLORS.primary;

    const durationStr = durationMs !== undefined
        ? durationMs < 1000 ? `${durationMs}ms` : `${(durationMs / 1000).toFixed(1)}s`
        : '';

    return (
        <Box flexDirection="column">
            {/* Header row: status icon + tool name + description + duration */}
            <Box>
                <Box minWidth={2}>
                    {status === 'pending' ? (
                        <Text color={statusColor}><InkSpinner type="dots" /></Text>
                    ) : (
                        <Text color={statusColor}>
                            {status === 'completed' ? TOOL_STATUS.SUCCESS : TOOL_STATUS.ERROR}
                        </Text>
                    )}
                </Box>
                <Text> </Text>
                <Text color={statusColor} bold={status === 'pending'}>{icon} {displayName}</Text>
                {serverName && serverName !== 'paean-system' && (
                    <Text dimColor> ({serverName})</Text>
                )}
                {description ? (
                    <Text dimColor> {description}</Text>
                ) : null}
                {status === 'completed' && (
                    <ResultSummary name={name} result={result} />
                )}
                {durationStr ? (
                    <Text dimColor> [{durationStr}]</Text>
                ) : null}
            </Box>
            {/* Inline result (collapsed diff/output) — only for completed */}
            {status === 'completed' && (
                <InlineResult name={name} result={result} />
            )}
        </Box>
    );
};

// ──────────────────────────────────────────────────────────────────
// Tool call group — bordered container for a turn's tool calls
// ──────────────────────────────────────────────────────────────────

interface ToolCallHistoryProps {
    history: ToolCallRecord[];
    compactMode?: boolean;
}

export const ToolCallHistory: React.FC<ToolCallHistoryProps> = ({ history, compactMode }) => {
    if (history.length === 0) return null;

    const completed = history.filter(h => h.status !== 'pending');
    const pending = history.find(h => h.status === 'pending');

    // In compact mode, show only a single summary line
    if (compactMode) {
        const doneCount = completed.length;
        const pendingName = pending ? formatToolName(pending.name) : null;
        return (
            <Box>
                {doneCount > 0 && (
                    <Text dimColor>{doneCount} tool{doneCount !== 1 ? 's' : ''} completed</Text>
                )}
                {pendingName && (
                    <>
                        {doneCount > 0 && <Text dimColor> · </Text>}
                        <Text color={COLORS.primary}><InkSpinner type="dots" /> {pendingName}</Text>
                    </>
                )}
            </Box>
        );
    }

    return (
        <Box
            flexDirection="column"
            borderStyle="round"
            borderColor={pending ? COLORS.primary : COLORS.muted}
            borderDimColor={!pending}
            paddingX={1}
        >
            {/* Completed tools */}
            {completed.map((entry) => (
                <ToolCallIndicator
                    key={entry.id}
                    name={entry.name}
                    type={entry.type}
                    serverName={entry.serverName}
                    status={entry.status}
                    args={entry.args}
                    result={entry.result}
                    durationMs={entry.durationMs}
                />
            ))}
            {/* Active/pending tool */}
            {pending && (
                <ToolCallIndicator
                    name={pending.name}
                    type={pending.type}
                    serverName={pending.serverName}
                    status="pending"
                    args={pending.args}
                />
            )}
        </Box>
    );
};
